import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, PlusCircle, Globe, LogOut, User as UserIcon } from "lucide-react";
import { LOGO_URL, CONTACT } from "@/data/constants";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const links = [
  { to: "/", key: "home" },
  { to: "/properties?purpose=sale", key: "buy" },
  { to: "/post-property", key: "sale" },
  { to: "/properties?purpose=rent", key: "rent" },
  { to: "/blog", key: "blog" },
  { to: "/contact", key: "contact" },
];

const LANGS = [
  { code: "en", label: "English", short: "EN" },
  { code: "gu", label: "ગુજરાતી", short: "ગુ" },
  { code: "hi", label: "हिंदी", short: "हि" },
];

export default function Navbar({ onLoginClick }) {
  const { t, lang, setLang } = useI18n();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-sm">
      <div className="max-w-[1400px] mx-auto px-4 py-3 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="nav-logo">
          <img src={LOGO_URL} alt="Krishna Property" className="h-14 w-14 object-contain rounded-full" />
          <div className="hidden sm:block leading-tight whitespace-nowrap">
            <div className="font-display font-bold text-base lg:text-lg text-kp-teal">KRISHNA PROPERTY</div>
            <div className="text-[9px] tracking-widest text-kp-maroon font-semibold">YOUR TRUST IS OUR SATISFACTION</div>
          </div>
        </Link>

        <nav className="hidden xl:flex items-center gap-0.5 text-[13px] font-medium">
          {links.map((l) => (
            <NavLink key={l.key} to={l.to} end={l.to === "/"} data-testid={`nav-${l.key}`}
              className={({ isActive }) => `px-2.5 py-2 rounded-md transition-colors whitespace-nowrap ${isActive ? "text-kp-maroon" : "text-slate-700 hover:text-kp-teal"}`}>
              {t(`nav.${l.key}`)}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link to="/post-property">
            <Button className="btn-maroon rounded-full hidden sm:inline-flex gap-2" data-testid="nav-post-property">
              <PlusCircle size={16}/> {t("nav.post")}
            </Button>
          </Link>
          {!user && (
            <Button variant="outline" className="hidden md:inline-flex border-kp-teal text-kp-teal hover:bg-kp-teal hover:text-white rounded-full" onClick={onLoginClick} data-testid="nav-login">
              {t("nav.login")}
            </Button>
          )}
          <button className="xl:hidden p-2 text-slate-700" onClick={() => setOpen(!open)} data-testid="nav-hamburger">
            {open ? <X/> : <Menu/>}
          </button>
        </div>
      </div>

      {open && (
        <div className="xl:hidden bg-white border-t border-slate-200 max-h-[75vh] overflow-y-auto">
          <div className="px-4 py-3 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink key={l.key} to={l.to} end={l.to === "/"} onClick={() => setOpen(false)}
                className={({ isActive }) => `px-3 py-2 rounded-md text-sm ${isActive ? "bg-kp-teal-light text-kp-teal" : "text-slate-700"}`}>
                {t(`nav.${l.key}`)}
              </NavLink>
            ))}

            {/* Language switcher — mobile */}
            <div className="mt-2 pt-2 border-t border-slate-100">
              <div className="px-3 pb-1.5 text-[11px] font-bold uppercase tracking-widest text-slate-400 flex items-center gap-1.5"><Globe size={12}/> Language</div>
              <div className="px-2 grid grid-cols-3 gap-1.5" data-testid="mobile-lang-selector">
                {LANGS.map((l) => (
                  <button key={l.code} onClick={() => { setLang(l.code); }} data-testid={`mobile-lang-${l.code}`}
                    className={`py-2 rounded-lg text-sm font-semibold border transition-colors ${lang === l.code ? "bg-kp-teal text-white border-kp-teal" : "bg-white text-slate-700 border-slate-200 hover:border-kp-teal"}`}>
                    {l.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Contact quick actions */}
            <div className="mt-2 pt-2 border-t border-slate-100 flex gap-2 px-2">
              <a href={`tel:${CONTACT.phoneRaw}`} className="flex-1 text-center py-2 rounded-lg bg-kp-teal-light text-kp-teal text-sm font-semibold" data-testid="mobile-call-link">Call</a>
              <a href={CONTACT.whatsappUrl} target="_blank" rel="noreferrer" className="flex-1 text-center py-2 rounded-lg bg-[#25D366] text-white text-sm font-semibold" data-testid="mobile-whatsapp-link">WhatsApp</a>
            </div>

            {/* Account row */}
            <div className="mt-2 pt-2 border-t border-slate-100 px-1">
              {user ? (
                <div className="flex items-center justify-between px-2">
                  <Link to={user.role === "admin" ? "/admin" : "/dashboard"} onClick={() => setOpen(false)} className="flex items-center gap-2 text-sm font-semibold text-kp-teal">
                    <UserIcon size={14}/> {user.name}
                  </Link>
                  <button onClick={() => { setOpen(false); logout(); }} className="text-xs text-slate-500 flex items-center gap-1"><LogOut size={13}/> Logout</button>
                </div>
              ) : (
                <button onClick={() => { setOpen(false); onLoginClick(); }} className="w-full text-left px-3 py-2 text-kp-teal text-sm font-semibold" data-testid="mobile-login-btn">{t("nav.login")}</button>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
